// systemCheckUtils.js
// Pure helper functions for the System Quality Check feature.
// No dependency on any existing interview file.

import * as faceapi from "face-api.js";
import {
    NETWORK_SAMPLE_COUNT,
    NETWORK_TIMEOUT_MS,
    MIC_SAMPLE_WINDOW_MS,
    FACE_API_MODEL_URL,
    FACE_DETECTION_SCORE_THRESHOLD,
} from "./systemCheckConstants";

/**
 * Maps a raw browser/DOM error into a candidate-friendly message.
 * Never surfaces raw error names (NotAllowedError, DOMException, etc.) to the UI.
 */
export function mapMediaError(err, kind = "camera") {
    const name = err?.name || "";
    const deviceWord = kind === "camera" ? "Camera" : "Microphone";

    if (name === "NotAllowedError" || name === "PermissionDeniedError") {
        return `${deviceWord} access is blocked. Allow ${kind} permission in your browser settings, then run the check again.`;
    }
    if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        return `We couldn't detect a ${kind} on this device. Please connect one and retry.`;
    }
    if (name === "NotReadableError" || name === "TrackStartError") {
        return `Your ${kind} may be in use by another application (Zoom, Teams, Meet, etc.). Close other video/audio apps and retry.`;
    }
    if (name === "OverconstrainedError") {
        return `Your ${kind} doesn't support the required settings. Try a different device if available.`;
    }
    if (name === "AbortError") {
        return `${deviceWord} initialization was interrupted. Please retry.`;
    }
    return `We ran into a problem accessing your ${kind}. Please check your device and retry.`;
}

/**
 * Validates basic camera track state. Relies entirely on readystate and enabled status
 * to maximize cross-browser compatibility, deferring the frame-generation proof to validateVideoDimensions.
 */
export function validateCameraTrack(videoTrack) {
    return !!(
        videoTrack &&
        videoTrack.readyState === "live" &&
        videoTrack.enabled
    );
}

/**
 * Verifies if an active HTMLVideoElement is producing valid visual dimensions.
 * This is a highly reliable check for actual frame generation.
 */
export function validateVideoDimensions(videoRef) {
    if (!videoRef || !videoRef.current) return false;
    const { videoWidth, videoHeight } = videoRef.current;
    return videoWidth > 0 && videoHeight > 0;
}

/**
 * Validates audio track parameters to ensure the microphone is alive and enabled.
 */
export function validateAudioTrack(audioTrack) {
    if (!audioTrack || audioTrack.readyState !== "live" || !audioTrack.enabled) {
        return false;
    }
    return true;
}

/**
 * Samples network latency by making N lightweight GET requests to `url` and
 * returns { medianMs, samples, saveData, effectiveType } without side effects.
 */
export async function sampleNetworkLatency(url, { count = NETWORK_SAMPLE_COUNT, timeoutMs = NETWORK_TIMEOUT_MS } = {}) {
    const connection =
        navigator.connection ||
        navigator.mozConnection ||
        navigator.webkitConnection;

    const networkInfo = {
        saveData: connection ? !!connection.saveData : false,
        effectiveType: connection ? connection.effectiveType || "unknown" : "unknown"
    };

    const samples = [];
    for (let i = 0; i < count; i++) {
        const controller = new AbortController();
        const timer = setTimeout(() => controller.abort(), timeoutMs);
        const start = performance.now();
        try {
            await fetch(url, { method: "GET", cache: "no-store", signal: controller.signal });
            samples.push(performance.now() - start);
        } catch {
            samples.push(timeoutMs);
        } finally {
            clearTimeout(timer);
        }
    }

    const sorted = [...samples].sort((a, b) => a - b);
    const medianMs = sorted.length % 2 === 0
        ? (sorted[sorted.length / 2 - 1] + sorted[sorted.length / 2]) / 2
        : sorted[Math.floor(sorted.length / 2)];

    return {
        medianMs: Math.round(medianMs),
        samples: samples.map(Math.round),
        ...networkInfo
    };
}

/**
 * Samples an audio MediaStream over a short window using Web Audio API and
 * returns whether meaningful (changing) audio energy was detected.
 */
export function sampleMicrophoneEnergy(stream, { windowMs = MIC_SAMPLE_WINDOW_MS } = {}) {
    return new Promise((resolve) => {
        let audioCtx;
        try {
            const AudioContextCls = window.AudioContext ?? window.webkitAudioContext;
            audioCtx = new AudioContextCls();
        } catch {
            resolve({ detected: false, peakLevel: 0, cleanup: () => {} });
            return;
        }

        const analyser = audioCtx.createAnalyser();
        analyser.fftSize = 1024;
        const source = audioCtx.createMediaStreamSource(stream);
        source.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);
        let rafId = null;
        let peakLevel = 0;
        let sampleCount = 0;
        let energySum = 0;
        const startedAt = performance.now();

        const cleanup = () => {
            if (rafId) cancelAnimationFrame(rafId);
            try { source.disconnect(); } catch {}
            try { analyser.disconnect(); } catch {}
            if (audioCtx && audioCtx.state !== "closed") {
                audioCtx.close().catch(() => {});
                audioCtx = null;
            }
        };

        const tick = () => {
            analyser.getByteTimeDomainData(dataArray);
            let sumSquares = 0;
            for (let i = 0; i < dataArray.length; i++) {
                const val = (dataArray[i] - 128) / 128;
                sumSquares += val * val;
            }
            const rms = Math.sqrt(sumSquares / dataArray.length);
            peakLevel = Math.max(peakLevel, rms);
            energySum += rms;
            sampleCount += 1;

            if (performance.now() - startedAt >= windowMs) {
                const avgLevel = sampleCount > 0 ? energySum / sampleCount : 0;
                resolve({ detected: peakLevel > 0.02 || avgLevel > 0.01, peakLevel, avgLevel, cleanup });
                return;
            }
            rafId = requestAnimationFrame(tick);
        };

        if (audioCtx.state === "suspended") {
            audioCtx.resume().catch(() => {});
        }
        rafId = requestAnimationFrame(tick);
    });
}

/** Stops all tracks on a MediaStream, defensively. */
export function stopAllTracks(stream) {
    if (!stream) return;
    try {
        stream.getTracks().forEach((t) => {
            try { t.stop(); } catch {}
        });
    } catch {}
}

/**
 * Feature-detects required browser APIs without changing its signature or causing side effects.
 * Uses `window.location` (never the bare `location` global) to satisfy the
 * no-restricted-globals ESLint rule enforced by this project's CRA lint config.
 */
export function detectBrowserCapabilities() {
    const hasWindow = typeof window !== "undefined";
    const hasWindowLocation = hasWindow && !!window.location;

    const isSecureContext = hasWindow && (
        window.isSecureContext ||
        (hasWindowLocation && (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1"))
    );

    const hasMediaDevices = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);

    const results = {
        mediaDevices: hasMediaDevices,
        webAudio: !!(window.AudioContext ?? window.webkitAudioContext),
        speechRecognition: !!(window.SpeechRecognition ?? window.webkitSpeechRecognition),
        speechSynthesis: !!window.speechSynthesis,
        mediaRecorder: !!window.MediaRecorder,
        permissionsApi: !!navigator.permissions,
        secureContext: isSecureContext
    };

    const hardRequirements = [results.mediaDevices, results.webAudio, results.mediaRecorder, results.secureContext];
    const allHardPass = hardRequirements.every(Boolean);
    return { ...results, allHardPass };
}

/** Classifies device/viewport while safely capturing platform information like system memory. */
export function detectDeviceCategory() {
    const ua = navigator.userAgent || "";
    const isMobileUA = /Mobi|Android(?!.*Tablet)|iPhone/i.test(ua);
    const isTabletUA = /iPad|Tablet|Android(?=.*Tablet)/i.test(ua) || (navigator.maxTouchPoints > 1 && /Macintosh/.test(ua));
    const width = window.innerWidth;

    let category = "desktop";
    if (isMobileUA) category = "mobile";
    else if (isTabletUA) category = "tablet";

    const memoryGB = typeof navigator.deviceMemory === "number" ? navigator.deviceMemory : null;

    return {
        category,
        width,
        height: window.innerHeight,
        memoryGB,
        isLowMemory: memoryGB !== null && memoryGB < 4
    };
}

/**
 * Validates snapshot dimensions purely and returns a clear status identifier string.
 */
export function validateSelfieDimensions(canvas) {
    if (!canvas || canvas.width <= 0 || canvas.height <= 0) {
        return "invalid_canvas";
    }
    if (canvas.width < 300 || canvas.height < 300) {
        return "too_small";
    }
    return "valid";
}

/**
 * Validates backend payloads safely via a stream clone to prevent stream exhaustion errors.
 * Ensures that both the status code represents success (2xx) and the body indicates data.success.
 */
export async function validateBackendPayload(response) {
    if (!response || typeof response.clone !== "function") return false;

    try {
        const clone = response.clone();
        const data = await clone.json();
        return response.ok && !!data?.success;
    } catch {
        return false;
    }
}

// ─── Face presence validation (verification photo) ─────────────────────────
// Uses the same face-api.js tiny-face-detector model already loaded elsewhere
// in the app (AIInterview.js) for in-interview monitoring. Loading it here too
// is safe and cheap — face-api.js tracks internally whether the model weights
// are already loaded and skips re-fetching them.

let faceApiModelsLoadPromise = null;

/**
 * Loads the tiny face detector model used to validate the verification photo.
 * Safe to call multiple times — the underlying network load only happens once.
 * Resolves to `true` once the model is ready, or `false` if loading failed
 * (in which case face-presence validation should be skipped rather than
 * permanently blocking the candidate — see detectFaceInImage's fail-open
 * behavior and how the caller uses this flag).
 */
export function loadFaceApiModels(modelUrl = FACE_API_MODEL_URL) {
    if (faceApiModelsLoadPromise) return faceApiModelsLoadPromise;
    faceApiModelsLoadPromise = (async () => {
        try {
            if (!faceapi.nets.tinyFaceDetector.isLoaded) {
                await faceapi.nets.tinyFaceDetector.loadFromUri(modelUrl);
            }
            return true;
        } catch (err) {
            console.error("System check: failed to load face detection models", err);
            faceApiModelsLoadPromise = null; // allow a retry on next call
            return false;
        }
    })();
    return faceApiModelsLoadPromise;
}

/**
 * Runs a single-face detection pass against a canvas/video/image element and
 * returns true only when a face is confidently detected. This is what stops a
 * hand, a blank wall, or an out-of-frame candidate from passing as a valid
 * verification photo.
 *
 * Fails OPEN (returns true) if detection itself throws — e.g. the model
 * failed to load or the runtime backend errors — so a detector problem never
 * permanently blocks a candidate who has otherwise passed every real check.
 */
export async function detectFaceInImage(canvasOrVideoOrImage) {
    try {
        const detection = await faceapi.detectSingleFace(
            canvasOrVideoOrImage,
            new faceapi.TinyFaceDetectorOptions({ inputSize: 320, scoreThreshold: FACE_DETECTION_SCORE_THRESHOLD })
        );
        return !!detection;
    } catch (err) {
        console.error("System check: face detection failed", err);
        return true;
    }
}